import {Plus, Trash2} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {
  TextField,
  RichTextEligibleField,
  StringListEditor,
  UseDefaultToggle,
  EditorCard,
  moveItem,
  sameConfig,
  type SectionProps,
} from '../controls';
import type {FaqItem, FaqSection as FaqSectionConfig} from '@common/enrollmentSite/types';
import {DEFAULT_FAQ_SECTIONS} from '@common/enrollmentSite/defaults/faq';

export function FaqSection({config, patch}: SectionProps) {
  const faq = config.faq;
  const usingDefault = faq === undefined;
  const sections = faq ?? [];

  function setSections(next: FaqSectionConfig[]) {
    patch({faq: next});
  }
  function updateSection(i: number, next: Partial<FaqSectionConfig>) {
    setSections(sections.map((s, idx) => (idx === i ? {...s, ...next} : s)));
  }
  function updateItem(si: number, ii: number, next: Partial<FaqItem>) {
    updateSection(si, {
      items: sections[si].items.map((it, idx) => (idx === ii ? {...it, ...next} : it)),
    });
  }
  function toggleDefault(useDefault: boolean) {
    if (useDefault) patch({faq: undefined});
    else setSections(structuredClone(DEFAULT_FAQ_SECTIONS));
  }

  return (
    <div className='space-y-5'>
      <UseDefaultToggle
        label='FAQ page'
        hint="The help-center questions. Uncheck to write this site's own sections and answers."
        usingDefault={usingDefault}
        isCustomized={!usingDefault && !sameConfig(faq, DEFAULT_FAQ_SECTIONS)}
        onChange={toggleDefault}
      />
      {usingDefault ? (
        <ul className='space-y-1 rounded-lg border bg-muted/20 p-3 text-sm text-muted-foreground'>
          {DEFAULT_FAQ_SECTIONS.map((s) => (
            <li key={s.label}>
              {s.label} <span className='text-xs'>· {s.items.length} questions</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className='space-y-3'>
          {sections.map((section, si) => (
            <EditorCard
              key={si}
              index={si}
              count={sections.length}
              title={section.label || 'Untitled section'}
              onMove={(dir) => setSections(moveItem(sections, si, dir))}
              onRemove={() => setSections(sections.filter((_, idx) => idx !== si))}
            >
              <TextField
                label='Section label'
                value={section.label}
                onChange={(label) => updateSection(si, {label})}
              />
              <div className='space-y-3'>
                {section.items.map((item, ii) => (
                  <div key={ii} className='space-y-3 rounded-md border bg-muted/10 p-3'>
                    <div className='flex items-center gap-2'>
                      <Input
                        value={item.question}
                        placeholder='Question'
                        onChange={(e) => updateItem(si, ii, {question: e.target.value})}
                      />
                      <Button
                        variant='ghost'
                        size='icon'
                        aria-label='Remove question'
                        onClick={() =>
                          updateSection(si, {items: section.items.filter((_, idx) => idx !== ii)})
                        }
                      >
                        <Trash2 className='h-4 w-4' />
                      </Button>
                    </div>
                    <RichTextEligibleField
                      label='Answer'
                      value={item.answer}
                      onChange={(answer) => updateItem(si, ii, {answer})}
                    />
                    <StringListEditor
                      label='Bullets'
                      items={item.bullets ?? []}
                      onChange={(bullets) =>
                        updateItem(si, ii, {bullets: bullets.length ? bullets : undefined})
                      }
                    />
                    <RichTextEligibleField
                      label='Closing line'
                      hint='Optional text after the bullets.'
                      value={item.closing}
                      onChange={(closing) => updateItem(si, ii, {closing: closing || undefined})}
                    />
                  </div>
                ))}
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() =>
                    updateSection(si, {items: [...section.items, {question: '', answer: ''}]})
                  }
                >
                  <Plus className='mr-1.5 h-3.5 w-3.5' />
                  Add question
                </Button>
              </div>
            </EditorCard>
          ))}
          <Button
            variant='outline'
            size='sm'
            onClick={() => setSections([...sections, {label: '', items: []}])}
          >
            <Plus className='mr-1.5 h-3.5 w-3.5' />
            Add section
          </Button>
        </div>
      )}
    </div>
  );
}
